import React, { useState, useEffect } from "react";
import { Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { CoreClassroomAdminClassroom, CoreClassroomAdminCourseState } from "../../../../models/CoreClassroomAdminClassroom";
import { ClassroomChangeStatus, ClearClassroomChangeStatus } from "../../../../store/ClassroomAdmin/ClassroomToolsAction";
import { RequestState, EmptyRequestState } from "../../../../models/RequestState";
import StatusCode from "../../../../util/StatusCode";
import Loading from "../../../misc/Loading";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SelectInput from "../../../misc/SelectInput";
import * as Helper from '../../../../util/Helper';


export const StatusOptions = [
    CoreClassroomAdminCourseState.Unknown,
    CoreClassroomAdminCourseState.Active,
    CoreClassroomAdminCourseState.Archived,
];

const ClassroomViewToolChangeStatus = () => {
    const { t } = useTranslation(['common', "classroomAdmin"]);
    const dispatch = useDispatch();
    const [status, setStatus] = useState<CoreClassroomAdminCourseState>(CoreClassroomAdminCourseState.Unknown);

    const classroom = useSelector<RootState, RequestState<CoreClassroomAdminClassroom>>(x => x.classroomAdminClassroom.classroom);
    const changeStatusClassroom = useSelector<RootState, EmptyRequestState>(x => x.classroomTools.changeStatusClassroom)

    useEffect(() => {
        return () => {
            ClearClassroomChangeStatus()(dispatch);
        };
    }, []);

    const updateChangeStatus = () => {
        if (classroom.code === StatusCode.COMPLETE && classroom.data && (status === CoreClassroomAdminCourseState.Archived || status === CoreClassroomAdminCourseState.Active)) {
            ClassroomChangeStatus(classroom.data.classroomId, status)(dispatch);
        }
    }

    const renderState = () => {
        if (changeStatusClassroom.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (changeStatusClassroom.code === StatusCode.COMPLETE) {
            return (<>
                <FontAwesomeIcon icon="check" color="green" style={{ marginRight: '5px' }} />
                {t('tools.CHANGE_STATUS.success', { ns: 'classroomAdmin' })}
            </>);
        }
        if (changeStatusClassroom.code === StatusCode.ERROR) {
            const errorData = changeStatusClassroom.error?.response?.data as { title?: string; detail?: string } | undefined;
            const title = errorData?.title;
            const errorMessage = title ? t(`tools.errors.general.${title}`, { ns: 'classroomAdmin' }) : t(`error.somethingWentWrong`, { ns: 'common' });
            return (<>
                <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                {errorMessage}
            </>);
        }
    }

    return (<>
        <div className="flex">
            <span className="w-25 mb-3"><b>{t('tools.CHANGE_STATUS.synched', { ns: 'classroomAdmin' })}:</b></span>
            <span>{classroom.code === StatusCode.COMPLETE && Helper.active(classroom.data.synched)}</span>
        </div>

        <div className="flex mb-3">
            <span className="w-25"><b>{t('tools.CHANGE_STATUS.currentStatus', { ns: 'classroomAdmin' })}:</b></span>
            <span>  {classroom.code === StatusCode.COMPLETE && t(`classroomAdmin.state.${classroom.data.state.courseState}`, { ns: 'classroomAdmin' })}</span>
        </div>

        <div className="mb-3">
            <span><b>{t('tools.CHANGE_STATUS.newStatus', { ns: 'classroomAdmin' })}:</b></span>
            <SelectInput
                type='number'
                items={StatusOptions
                    .filter(x => x !== CoreClassroomAdminCourseState.Unknown)
                    .filter(x => (classroom.code === StatusCode.COMPLETE && !classroom.data.synched) || x !== CoreClassroomAdminCourseState.Archived)
                    .map(key => ({
                        value: key,
                        text: t(`classroomAdmin.state.${CoreClassroomAdminCourseState[key]}`, { ns: 'classroomAdmin' })
                    }))}
                onChange={(_, value) => {
                    if (value !== undefined) {
                        setStatus(value as CoreClassroomAdminCourseState);
                    }
                }}
                name='status'
                selected={status}
                defaultText={t(`tools.CHANGE_STATUS.placeholderSelect`, { ns: 'classroomAdmin' })}
            />
        </div>

        {(classroom.code === StatusCode.COMPLETE && classroom.data.synched)
            ? <div className="mb-3" style={{ padding: '20px 40px', border: '1px solid red', margin: '0 20px', backgroundColor: 'pink' }}>
                {t(`tools.CHANGE_STATUS.warning`, { ns: 'classroomAdmin' })}</div>
            : null}

        <div className="flex align-items-center">
            <Button onClick={() => updateChangeStatus()} disabled={!status} style={{ marginRight: '15px' }}>{t('action.confirm', { ns: 'common' })}</Button>
            {renderState()}
        </div>
    </>);
}

export default ClassroomViewToolChangeStatus;
